import { useMemo } from 'react';
import { isEmpty, isNumber } from 'lodash-es';
import { blockchainTokensMap } from '../utils/blockchain-consts.ts';
import { isBigInt } from '../utils/helpers.ts';
import { HorseName } from '../utils/racing-game/consts.ts';
import {
  calcMultiplier,
  getMultiplierRatioByPlace,
  getTokenGroup,
} from '../utils/racing-game/helpers.ts';
import { useGameRoundDataBlockchainQuery } from '../services/queries';
import { useGetRoundNumberByType } from './useGetRoundNumberByType.ts';

export const usePreviousGameRoundBlockchainResults = (enabled?: boolean) => {
  const prevRoundNumber = useGetRoundNumberByType('prev');

  const { data: blockchainData } = useGameRoundDataBlockchainQuery(
    prevRoundNumber ? BigInt(prevRoundNumber) : null,
    {
      enabled: !!enabled && !!prevRoundNumber,
    }
  );

  const tokens = useMemo(() => {
    if (enabled === false || !blockchainData) return [];

    const { startPrices, endPrices, totalBetsAmount, betAmounts } =
      blockchainData;

    if (debugResults) {
      console.log('🔍 Blockchain data debug:', {
        totalBetsAmount,
        betAmounts,
        hasTotalBetsAmount: isBigInt(totalBetsAmount),
        betAmountsTypes: betAmounts?.map((amount) => ({
          value: amount,
          type: typeof amount,
          isBigInt: isBigInt(amount),
          asNumber: isBigInt(amount) ? Number(amount) : amount,
        })),
      });
    }

    const tokens = blockchainTokensMap
      .map((token, index) => {
        const startPrice = isBigInt(startPrices[index])
          ? Number(startPrices[index])
          : null;
        const endPrice = isBigInt(endPrices[index])
          ? Number(endPrices[index])
          : null;
        const growth =
          startPrice && endPrice
            ? ((endPrice - startPrice) / startPrice) * 100
            : undefined;

        const multiplier = calcMultiplier(totalBetsAmount, betAmounts[index], {
          multiplierRatioPlace: null,
        });

        if (debugResults) {
          console.log(`🐎 Game round results: Token ${token}:`, {
            betAmount: betAmounts[index],
            isBetAmountValid: isBigInt(betAmounts[index]),
            multiplier,
            growth,
          });
        }

        return {
          token,
          startPrice,
          endPrice,
          growth,
          multiplier,
        };
      })
      .filter(({ growth }) => isNumber(growth));

    if (tokens.length > 0 && tokens.length !== blockchainTokensMap.length) {
      console.warn('NOT ALL TOKENS HAVE GROWTH, RESULT IS OMITTED', tokens);
      return [];
    }

    return tokens;
  }, [enabled, blockchainData]);

  const top3 = useMemo(() => {
    if (enabled === false) return [];

    return tokens
      .sort((a, b) => b.growth! - a.growth!)
      .slice(0, 3)
      .map(({ token, multiplier }, index) => {
        const finalMultiplier = multiplier * getMultiplierRatioByPlace(index);

        if (debugResults) {
          console.log(`🏆 Top3 ${index + 1} (${token}):`, {
            originalMultiplier: multiplier,
            ratio: getMultiplierRatioByPlace(index),
            finalMultiplier,
          });
        }

        return {
          token: token as HorseName,
          multiplier: finalMultiplier,
        };
      });
  }, [enabled, tokens]);

  const winnerGroup = useMemo(() => {
    if (enabled === false || isEmpty(top3) || !blockchainData) return null;

    const groupName = getTokenGroup(top3[0].token);

    const { totalGroupBetsAmount, groupBetAmounts } = blockchainData;
    const groupBetAmount = groupBetAmounts[groupName === 'A' ? 0 : 1];
    const multiplier = calcMultiplier(totalGroupBetsAmount, groupBetAmount, {
      multiplierRatioPlace: null,
    });

    if (debugResults) {
      console.log(`🏆 Winner group ${groupName}:`, {
        multiplier,
      });
    }

    return {
      groupName,
      multiplier,
    };
  }, [enabled, top3, blockchainData]);

  return useMemo(
    () => ({
      round: Number(prevRoundNumber),
      top3,
      winnerGroup,
    }),
    [prevRoundNumber, top3, winnerGroup]
  );
};

const debugResults = false;
